import { BaseApiService } from '@/services/base-service';
import {
  ApiPagination, BaseResponse, Filters, FiltersPagination, Response,
} from '@/types';
import { ConferencesStatuses } from '@/enums';
import { Conferences } from '../types';

interface ConferencesList extends BaseResponse, ApiPagination {
  data: {
    items: Conferences.Conference[],
    organizations: string[],
    statuses: string[]
  }
}

interface NewConferenceResponse {
  data: Conferences.NewConferenceRespone
}

interface ConferenceDetail extends BaseResponse, ApiPagination {
  data: Conferences.ConferenceDetail
}

export class ConferenceService extends BaseApiService {
  private readonly base_path = 'conferences';

  async get_conferences(filters: FiltersPagination<Filters>): Promise<ConferencesList> {
    const url = `${this.base_path}/requests`;
    const payload = filters;

    const { data } = await this.http_client.get<ConferencesList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async send_conference(payload: Conferences.NewConference): Promise<number | undefined> {
    const url = `${this.base_path}/requests`;

    const { data } = await this.http_client.post<Response<null>>(url, payload);

    return data.code;
  }

  async upd_conference(id: string| null, payload: Conferences.NewConference): Promise<number | undefined> {
    const url = `${this.base_path}/requests/${id}`;

    const { data } = await this.http_client.patch<Response<null>>(url, payload);

    return data.code;
  }

  async get_conference_by_id(id: string | number, config = {}): Promise<NewConferenceResponse> {
    const url = `${this.base_path}/${id}`;

    const { data } = await this.http_client.get<NewConferenceResponse>(url, config);

    return data;
  }

  async get_conference_request_by_id(id: string | number): Promise<ConferenceDetail> {
    const url = `${this.base_path}/requests/${id}`;

    const { data } = await this.http_client.get<ConferenceDetail>(url);

    return data;
  }

  async record_stat(id: string | number): Promise<number | undefined> {
    const url = `${this.base_path}/${id}/stat`;

    const { data } = await this.http_client.post<Response<null>>(url, {
      type: 'following_link',
    });

    return data.code;
  }
}

export const default_new_conference_response = (): Conferences.NewConferenceRespone => ({
  title: '',
  url: '',
  start_registration_time: '',
  start_registration_date: '',
  image_path: '',
  image_original_name: '',
  id: 0,
  end_registration_time: '',
  description: '',
  nomenclatures: [],
  end_registration_date: '',
  status: {
    id: 0,
    slug: '',
    name: '',
  },
  type: {
    id: 0,
    slug: '',
    name: '',
  },
});

export const default_new_conference = (): Conferences.NewConference => ({
  organization_id: 0,
  conference_id: 0,
  organization_print: '',
  users: [],
  type: {
    id: 0,
    slug: '',
    name: '',
  },
});

export const default_conference = (): Conferences.ConferenceDetail => ({
  conference: {
    description: '',
    id: 0,
    title: '',
    nomenclatures: [],
    type: {
      id: 0,
      slug: '',
      name: '',
    },
  },
  date: '',
  id: 0,
  nomenclature: {
    id: 0,
    title: '',
    code: '',
    price: '',
    min_staff_count: 0,
    max_staff_count: 0,
    nomenclature: '',
  },
  organization: {
    id: 0,
    name_organization: '',
  },
  status: {
    id: 0,
    name: '',
    slug: ConferencesStatuses.IN_PROGRESS,
  },
  users: [],
  number: 0,
  order_external_number: 0,
  order_id: null,
  comment: '',
  organization_print: '',
});
